/**
 * SICA glossary — terms the brand mandates as primary vocabulary
 * (docs/about/vision.md "Conceptos clave que la marca debe poder nombrar").
 */

export type GlossaryTerm = {
  id: string;
  term: string;
  short?: string;
  definition: string;
  example?: string;
  related?: ReadonlyArray<string>;
};

export const GLOSSARY: readonly GlossaryTerm[] = [
  {
    id: 'autoridad-semantica',
    term: 'Autoridad semántica',
    definition:
      'Capacidad reservada al Core de SICA para construir, validar y fijar el significado curricular. La UI lo consume; nunca lo crea ni lo infiere.',
    related: ['Core', 'UI Docente'],
  },
  {
    id: 'capacidad',
    term: 'Capacidad',
    definition:
      'Tipo de aprendizaje priorizado por algunos diseños jurisdiccionales que enfatiza el "saber hacer" del estudiante (resolver problemas, comunicar, comprender textos, etc.).',
    related: ['Saber', 'Indicador de progreso'],
  },
  {
    id: 'cita',
    term: 'Cita',
    short: 'fuente verificable',
    definition:
      'Referencia textual a un fragmento de un documento normativo, indicando documento, página y jurisdicción. SICA no responde sin cita.',
    related: ['Política de citas', 'IAC'],
  },
  {
    id: 'core',
    term: 'Core',
    definition:
      'Backoffice de SICA. Ingiere documentos, construye la jerarquía curricular, valida relaciones y publica intelligence consumible. Único locus de autoridad semántica.',
    related: ['UI Docente', 'Curaduría humana'],
  },
  {
    id: 'curaduria-humana',
    term: 'Curaduría humana',
    definition:
      'Validación realizada por especialistas curriculares sobre la jerarquía y las relaciones extraídas por el procesamiento automático, antes de que la información llegue al motor de consulta.',
    related: ['Autoridad semántica'],
  },
  {
    id: 'eje',
    term: 'Eje',
    definition:
      'Primer nivel de organización dentro de un área curricular en los NAP y en muchos diseños jurisdiccionales. Agrupa bloques de saberes/capacidades afines.',
    related: ['Bloque', 'Saber'],
  },
  {
    id: 'bloque',
    term: 'Bloque',
    definition:
      'Subdivisión de un eje. Agrupa saberes y capacidades específicas que se desarrollan en un ciclo o nivel.',
    related: ['Eje'],
  },
  {
    id: 'ciclo',
    term: 'Ciclo',
    definition:
      'Tramo del nivel obligatorio: 1.er, 2.º o 3.er ciclo, según el nivel y la jurisdicción.',
  },
  {
    id: 'diseno-jurisdiccional',
    term: 'Diseño jurisdiccional',
    short: 'norma provincial',
    definition:
      'Documento curricular publicado por una jurisdicción (provincia o CABA) que desarrolla, en su territorio, los aprendizajes acordados a nivel nacional. No es un NAP.',
    related: ['NAP', 'Jurisdicción'],
  },
  {
    id: 'iac',
    term: 'IAC',
    short: 'Inventario de Activos Curriculares',
    definition:
      'Registro inmutable y auditable de cada documento fuente que SICA usa: hash, snapshot, URL original y fecha de captura. Base de la trazabilidad.',
    related: ['Trazabilidad', 'Política de citas'],
  },
  {
    id: 'implementacion',
    term: 'Implementación',
    definition:
      'En el modelo conceptual de SICA, la realización concreta de un diseño curricular dentro de una institución o aula. SICA modela las jurisdicciones; las implementaciones quedan en el ámbito docente.',
  },
  {
    id: 'indicador-de-progreso',
    term: 'Indicador de progreso',
    definition:
      'Descriptor que enuncia qué se espera que un estudiante demuestre haber aprendido al cierre de un ciclo. Forma parte de los NAP y de varios diseños jurisdiccionales.',
  },
  {
    id: 'inteligencia-curricular-validada',
    term: 'Inteligencia curricular validada',
    definition:
      'La categoría descriptiva de SICA. No es un asistente, ni un tutor, ni un buscador: es un sistema que expone normativa con jerarquía preservada y cita exacta.',
  },
  {
    id: 'jerarquia-federal',
    term: 'Jerarquía federal',
    definition:
      'La estructura curricular argentina, en la que NAP nacionales y diseños jurisdiccionales coexisten por mandato del federalismo. SICA preserva la jerarquía: no la colapsa.',
  },
  {
    id: 'jurisdiccion',
    term: 'Jurisdicción',
    definition:
      'Cada provincia argentina y la Ciudad Autónoma de Buenos Aires, en cuanto autoridades educativas con potestad sobre sus diseños curriculares.',
  },
  {
    id: 'mcp',
    term: 'MCP',
    short: 'Model Context Protocol',
    definition:
      'Protocolo abierto que permite a un agente externo (Claude, ChatGPT, etc.) consultar herramientas tipadas. SICA expone su intelligence vía MCP, conservando garantías de cita.',
  },
  {
    id: 'modalidad',
    term: 'Modalidad',
    definition:
      'Forma específica de cursar el sistema obligatorio: común, especial, técnica-profesional, rural, intercultural bilingüe, en contextos de privación de libertad, domiciliaria/hospitalaria, jóvenes y adultos.',
  },
  {
    id: 'nap',
    term: 'NAP',
    short: 'Núcleos de Aprendizajes Prioritarios',
    definition:
      'Acuerdos curriculares federales aprobados por el Consejo Federal de Educación. Son referencia común para todo el país y garantía de un piso compartido de aprendizajes.',
    related: ['Diseño jurisdiccional', 'Jerarquía federal'],
  },
  {
    id: 'nivel',
    term: 'Nivel',
    definition:
      'Cada uno de los tramos del sistema educativo: inicial, primaria, secundaria, superior. SICA cubre los obligatorios.',
  },
  {
    id: 'politica-de-citas',
    term: 'Política de citas',
    definition:
      'Restricción operativa de SICA: ninguna respuesta sin documento y página verificables. Si no hay fuente, SICA dice que no sabe.',
    related: ['Cita', 'IAC'],
  },
  {
    id: 'saber',
    term: 'Saber',
    definition:
      'Aprendizaje específico priorizado por los NAP o por un diseño jurisdiccional. Junto con la capacidad, es el grano fino de la organización curricular.',
    related: ['Capacidad'],
  },
  {
    id: 'trazabilidad',
    term: 'Trazabilidad',
    definition:
      'Garantía de que cada afirmación curricular en SICA puede ser rastreada hasta un documento fuente, página y jurisdicción específicos.',
    related: ['IAC', 'Cita'],
  },
  {
    id: 'ui-docente',
    term: 'UI Docente',
    definition:
      'Aplicación interna de SICA dirigida al docente autenticado. Consume la intelligence validada por el Core; no infiere ni curva la verdad.',
    related: ['Core', 'Autoridad semántica'],
  },
];
